// The seven shield auras of the world theme (R831, `ui-map-interface.md`
// §6.1).
//
// A set Tender lays one mark over the water it covers. The AUTO mark is the
// regen wave of mockup round thirty-four card 14, and the world theme
// offers six alternatives: cards 11, 12, 13, 15, 17 and 20. Each one
// REPLACES the wave; none stacks on it.
//
// This is a leaf (`leaves.test.ts`). It holds the arithmetic and the draw
// order of each aura, and it names the three sinks of a layer through a
// type-only clause, the way `chart-glyphs.ts` does: a type is erased before
// the bundler sees it, so the module still weighs nothing but itself. The
// fleet layer owns the state, the cull, the atlas and the hex walk, and it
// hands them in as one reused `AuraCtx`.
//
// **Nothing here allocates.** A style runs once a set Tender a frame, so
// every scratch is a module constant and every walk goes through a callback
// the layer owns (§10).

import { FLEET_AURA, FLEET_TENDER } from '../gen/presentation';
import { COL_PITCH, HEX_SIZE } from '../gen/balance';
import type { AuraStyle } from '../ui/settings';
import type { CircleSink, QuadSink, SegmentSink, SpriteEntry } from '../fast/layers/batches';
import { cellAt, COLS, ROWS } from '../world';
import { TAU, cellScale } from './recipe-kit';
import { waveAt, waveU, type WaveRing } from './tender';

/** The figures of the six alternatives, as the pack emits them. */
export type AuraFigures = typeof FLEET_AURA;

/**
 * What a style may draw with.
 *
 * The layer fills one of these in and hands the SAME object to every
 * Tender of the frame, so a style must read it and keep no reference to
 * it.
 */
export interface AuraCtx {
  /** The Tender, in world px. */
  x: number;
  y: number;
  /** The reach in world px, rounded to a whole px (see `auraR`). */
  R: number;
  /** Where a wave ring is born: the anchor ring's radius. */
  r0: number;
  /** The layer's clock, in seconds. */
  t: number;
  /** This Tender's phase, 0 to 1, so a row of them does not pulse in step. */
  phase: number;
  /** The Tender's fleet id, which names its berth on the wire (R723). */
  id: number;
  /** The ink every stroke wears, from `FLEET_TENDER.wave.ink`. */
  color: number;
  /** The Tender's own fade. Every alpha is multiplied by it. */
  alpha: number;
  rings: CircleSink;
  quads: QuadSink;
  segs: SegmentSink;
  /** The map's own hex, baked white, and its stroked twin. */
  hex: SpriteEntry;
  hexRim: SpriteEntry;
  /** The soft pip the control layer already owns. */
  pip: SpriteEntry;
  /** An atlas cell baked on demand, for a key `auraCellKey` builds. */
  sprite(key: string): SpriteEntry;
  /**
   * Every own hull of this Tender's berth, the Tender itself left out.
   * `k` is the hull's rank in the berth and `hidden` says the hull is off
   * the glass.
   */
  members(fn: (hx: number, hy: number, k: number, hidden: boolean) => void): void;
  /**
   * Every map cell whose centre lies inside the reach AND on the frame,
   * with its distance from the Tender.
   */
  cells(fn: (cx: number, cy: number, d: number) => void): void;
  /** Is the Tender itself off the glass? A spoke still draws to a hull on it. */
  hidden: boolean;
  /** The figures of the six alternatives. */
  f: AuraFigures;
}

/** One ring of the wave, reused so no frame allocates. */
const RING: WaveRing = { r: 0, alpha: 0, width: 0 };

/** The breath of the mockup page: `p = 0.5 + 0.5 sin(2 pi hz t)`, 0 to 1. */
export function auraPulse(t: number, hz: number): number {
  return 0.5 + 0.5 * Math.sin(TAU * hz * t);
}

/**
 * The weight of the hex-cells ripple at distance `d`, 0 to 1.
 *
 * The crest travels outward, because `d / R` is subtracted from the clock:
 * a cell at the rim lights one whole period after the cell at the centre.
 * The negative half of the sine is cut, so a cell rests at its base alpha
 * for half of every period.
 */
export function auraRipple(t: number, d: number, R: number, periodSec: number): number {
  return Math.max(0, Math.sin(TAU * (t / periodSec - d / R)));
}

/**
 * The largest baked aura cell, in texels.
 *
 * The bubble and the mesh bake ONE cell per reach, `2R + 2` texels a side
 * — the reach both ways plus a texel of margin. The `fog` cell is 256, so
 * that is the ceiling this shares with it. A reach past it draws the AUTO
 * wave instead, which needs no cell at all.
 */
export const AURA_CELL_MAX_PX = 256;

/** The margin a baked cell carries round the reach, in texels. */
export const AURA_CELL_PAD_PX = 2;

/** The side of the baked cell of a reach, in texels. */
export function auraCellSize(R: number): number {
  return 2 * R + AURA_CELL_PAD_PX;
}

/** Whether a reach still fits one baked cell. */
export function auraCellFits(R: number): boolean {
  return Number.isInteger(R) && R > 0 && auraCellSize(R) <= AURA_CELL_MAX_PX;
}

/**
 * The atlas key of the cell a bubble or a mesh bakes at reach `R`.
 *
 * The scheme lives here and not in the atlas, because the leaf asks for
 * the key and `fast/gfx/atlas.ts` answers it: one spelling, two readers.
 */
export function auraCellKey(kind: 'bubble' | 'mesh', R: number): string {
  const keys = AURA_KEYS[kind];
  let key = keys.get(R);
  if (key === undefined) {
    key = `aura:${kind}:${R}`;
    keys.set(R, key);
  }
  return key;
}

/** Each key `auraCellKey` built, by reach, so a frame builds no string. */
const AURA_KEYS = { bubble: new Map<number, string>(), mesh: new Map<number, string>() };

/** The reach the marks use: a whole px, so a baked cell is stable. */
export function auraR(reachPx: number): number {
  return Math.round(reachPx);
}

/**
 * Every map cell whose centre lies inside the reach, as `x, y, d` triples
 * written into `out`, in ascending cell index. The count comes back.
 *
 * The walk reads the rows and the columns of the box round the reach, so it
 * builds no cell list (§10.1). The centre of a cell is `world.ts
 * cellCenter` written out, because that function answers with a fresh pair
 * and this walk must not allocate one a cell a frame. `aura-styles.test.ts`
 * pins the two against each other.
 */
export function auraCells(x: number, y: number, R: number, out: number[]): number {
  if (cellAt(x, y) < 0 || R <= 0) return 0;
  // `COLS` and `ROWS` are live bindings: `world.ts` re-points them when the
  // server names the grid, and an ES import reads the new value.
  const cols = COLS, rows = ROWS, pitchY = 1.5 * HEX_SIZE;
  // The box is one row and one column wider than the reach on each side,
  // so the odd-row shift and a rounding at the edge lose no cell.
  const row0 = Math.max(0, Math.floor((y - R - HEX_SIZE) / pitchY) - 1);
  const row1 = Math.min(rows - 1, Math.ceil((y + R - HEX_SIZE) / pitchY) + 1);
  const col0 = Math.max(0, Math.floor((x - R) / COL_PITCH) - 1);
  const col1 = Math.min(cols - 1, Math.ceil((x + R) / COL_PITCH) + 1);
  let n = 0;
  for (let row = row0; row <= row1; row++) {
    const cy = pitchY * row + HEX_SIZE;
    for (let col = col0; col <= col1; col++) {
      const cx = COL_PITCH * (col + 0.5 * (row & 1) + 0.5);
      const d = Math.hypot(cx - x, cy - y);
      if (d > R) continue;
      out[n * 3] = cx;
      out[n * 3 + 1] = cy;
      out[n * 3 + 2] = d;
      n++;
    }
  }
  return n;
}

/**
 * The aura the two visitors below draw for.
 *
 * `hex-cells` and `spokes` hand a visitor to the layer. A closure over the
 * context would be one allocation an aura a frame (R131), so the visitors are
 * module functions and the style names its context here before the walk. The
 * layer walks inside the call, and one style never runs inside another walk.
 */
let CUR: AuraCtx;

/** One cell of `hex-cells`: the fill and the rim, on the ripple. */
function hexCell(cx: number, cy: number, d: number): void {
  const a = CUR, f = a.f.hexCells;
  const w = auraRipple(a.t, d, a.R, f.rippleSec);
  a.quads.sprite(cx, cy, 0, a.hex, a.color, (f.fillAlpha + f.fillSwing * w) * a.alpha);
  a.quads.sprite(cx, cy, 0, a.hexRim, a.color, (f.edgeAlpha + f.edgeSwing * w) * a.alpha);
}

/** One spoke of `spokes`: the line to the hull, and the pip that runs on it. */
function spoke(hx: number, hy: number, k: number, hidden: boolean): void {
  const a = CUR, f = a.f.spokes;
  // A spoke crosses the window when ONE of its ends is on it.
  if (hidden && a.hidden) return;
  a.segs.line(a.x, a.y, hx, hy, f.lineW, a.color, f.lineAlpha * a.alpha);
  let u = a.t / f.periodSec + k * f.stagger;
  u -= Math.floor(u);
  a.quads.sprite(a.x + (hx - a.x) * u, a.y + (hy - a.y) * u, 0,
    a.pip, a.color, f.pipAlpha * a.alpha, cellScale(a.pip, f.pipR));
}

/** One aura, drawn into the sinks the context carries. */
export type AuraStyleFn = (a: AuraCtx) => void;

/**
 * The seven auras, by id.
 *
 * `regen-wave` is AUTO. It is the body the fleet layer drew before the
 * world theme had a shield-aura field, moved here unchanged: rings born at
 * the anchor ring, grown to the reach, thinning and fading to nothing
 * exactly at the edge, so the aura carries NO rim.
 */
export const AURA_STYLES: Record<AuraStyle, AuraStyleFn> = {
  'regen-wave'(a) {
    const w = FLEET_TENDER.wave;
    for (let k = 0; k < w.rings; k++) {
      const u = waveU(a.t, k, w.rings, w.periodSec, a.phase);
      const r = waveAt(u, a.r0, a.R, w.alpha, w.width0, w.width1, RING);
      a.rings.ring(a.x, a.y, r.r, r.width, a.color, r.alpha * a.alpha);
    }
  },

  /** Card 11: the sonar layer's own shape, a fill under a breathing ring. */
  'coverage-disc'(a) {
    const f = a.f.coverageDisc;
    a.rings.disc(a.x, a.y, a.R, a.color, f.discAlpha * a.alpha);
    const ring = f.ringAlpha - f.ringSwing * auraPulse(a.t, f.breatheHz);
    a.rings.ring(a.x, a.y, a.R, f.ringW, a.color, ring * a.alpha);
  },

  /** Card 12: a dome seen from above, with one sheen that turns. */
  bubble(a) {
    if (!auraCellFits(a.R)) return AURA_STYLES['regen-wave'](a);
    const f = a.f.bubble;
    a.quads.sprite(a.x, a.y, 0, a.sprite(auraCellKey('bubble', a.R)), a.color, a.alpha);
    a.rings.ring(a.x, a.y, a.R, f.ringW, a.color, f.ringAlpha * a.alpha);
    const start = (a.t * TAU) / f.sheenPeriodSec;
    a.rings.arc(a.x, a.y, a.R - f.sheenInsetPx, f.sheenW,
      start % TAU, f.sheenSweep, a.color, f.sheenAlpha * a.alpha);
  },

  /** Card 13: the rim alone, a dashed ring that turns. */
  'lattice-ring'(a) {
    const f = a.f.latticeRing;
    a.rings.disc(a.x, a.y, a.R, a.color, f.discAlpha * a.alpha);
    const period = f.dashOn + f.dashOff;
    // The dash keeps its LENGTH at every reach, so the count is the
    // circumference divided by one dash and one gap.
    const count = Math.max(1, Math.round((TAU * a.R) / period));
    // The circle batch has no dash phase, so the whole ring turns
    // instead. An arc length of `speedPxPerSec t` is `speedPxPerSec t / R` radians.
    const start = -(f.speedPxPerSec * a.t) / a.R;
    a.rings.dashed(a.x, a.y, a.R, f.dashW, count, f.dashOn / period,
      a.color, f.dashAlpha * a.alpha, start);
  },

  /** Card 15: the aura drawn the way sovereignty is drawn. */
  'hex-cells'(a) {
    CUR = a;
    a.cells(hexCell);
  },

  /** Card 17: one spoke to every hull of the berth. No circle. */
  spokes(a) {
    CUR = a;
    a.members(spoke);
  },

  /** Card 20: chain mail seen from above, breathing. */
  mesh(a) {
    if (!auraCellFits(a.R)) return AURA_STYLES['regen-wave'](a);
    const f = a.f.mesh;
    const br = f.breatheFloor + (1 - f.breatheFloor) * auraPulse(a.t, f.breatheHz);
    // The weave is baked white at full alpha, so one cell serves every
    // breath: the quad carries `lineAlpha` and the breath together.
    a.quads.sprite(a.x, a.y, 0, a.sprite(auraCellKey('mesh', a.R)),
      a.color, f.lineAlpha * br * a.alpha);
    a.rings.disc(a.x, a.y, a.R, a.color, f.discAlpha * br * a.alpha);
    a.rings.ring(a.x, a.y, a.R, f.rimW, a.color, f.rimAlpha * br * a.alpha);
  },
};
